import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { ArrowLeft, Download, Loader2, RefreshCw, Send } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import Content from '~/components/layout/Content';

import { invoiceTypeLabels } from '~/lib/invoice-type-config';

const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(num);
};

const Invoice = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [showSendDialog, setShowSendDialog] = useState(false);

    const { data: invoice, isLoading, error } = trpc.invoices.getOne.useQuery(
        { id: id! },
        { enabled: !!id }
    );

    const downloadMutation = trpc.invoices.getDownloadUrl.useMutation({
        onSuccess: (data) => {
            window.open(data.downloadUrl, '_blank');
        },
        onError: (error) => {
            toast.error(`Fehler beim Download: ${error.message}`);
        },
    });

    const sendInvoiceMutation = trpc.invoices.sendInvoice.useMutation({
        onSuccess: () => {
            setShowSendDialog(false);
            toast.success('Rechnung wurde an den Kunden gesendet.');
        },
        onError: (error) => {
            toast.error(`Fehler beim Senden: ${error.message}`);
        },
    });

    if (isLoading) {
        return (
            <Content>
                <div className='container mx-auto py-10'>
                    <Skeleton className='h-8 w-48 mb-6' />
                    <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
                        <div className='lg:col-span-2 space-y-6'>
                            <Skeleton className='h-48' />
                            <Skeleton className='h-64' />
                        </div>
                        <div>
                            <Skeleton className='h-48' />
                        </div>
                    </div>
                </div>
            </Content>
        );
    }

    if (error || !invoice) {
        return (
            <Content>
                <div className='container mx-auto py-10'>
                    <div className='text-center'>
                        <h1 className='text-2xl font-bold mb-4'>Rechnung nicht gefunden</h1>
                        <Button onClick={() => navigate('/invoices')}>
                            <ArrowLeft className='mr-2 h-4 w-4' />
                            Zurück zur Übersicht
                        </Button>
                    </div>
                </div>
            </Content>
        );
    }

    const typeInfo = invoiceTypeLabels[invoice.type] || { label: invoice.type, variant: 'outline' as const };
    const isRefund = invoice.type.includes('REFUND');

    return (
        <Content>
            <div className='container mx-auto py-10'>
                {/* Header */}
                <div className='flex items-center justify-between mb-6'>
                    <div className='flex items-center gap-4'>
                        <Button variant='ghost' size='icon' onClick={() => navigate('/invoices')}>
                            <ArrowLeft className='h-4 w-4' />
                        </Button>
                        <div>
                            <h1 className='text-2xl font-bold'>{invoice.invoiceNumber}</h1>
                            <p className='text-muted-foreground'>
                                Rechnung vom {format(new Date(invoice.invoiceDate), 'dd. MMMM yyyy', { locale: de })}
                            </p>
                        </div>
                    </div>
                    <div className='flex items-center gap-2'>
                        <Badge variant={typeInfo.variant} className='text-sm'>
                            {typeInfo.label}
                        </Badge>
                        <Button
                            onClick={() => downloadMutation.mutate({ id: invoice.id })}
                            disabled={downloadMutation.isPending}
                        >
                            {downloadMutation.isPending ? (
                                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            ) : (
                                <Download className='mr-2 h-4 w-4' />
                            )}
                            PDF herunterladen
                        </Button>
                        <Button
                            variant='secondary'
                            onClick={() => setShowSendDialog(true)}
                            disabled={sendInvoiceMutation.isPending}
                        >
                            {sendInvoiceMutation.isPending ? (
                                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            ) : (
                                <Send className='mr-2 h-4 w-4' />
                            )}
                            Rechnung senden
                        </Button>
                    </div>
                </div>

                <AlertDialog open={showSendDialog} onOpenChange={setShowSendDialog}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Rechnung an Kunden senden?</AlertDialogTitle>
                            <AlertDialogDescription asChild>
                                <div>
                                    Die Rechnung {invoice.invoiceNumber} inklusive PDF-Download-Link wird an den
                                    Kunden gesendet:
                                    <div className='mt-3 space-y-1 text-foreground'>
                                        {invoice.billingDraft?.customer && (
                                            <div className='font-medium'>
                                                {invoice.billingDraft.customer.firstName}{' '}
                                                {invoice.billingDraft.customer.lastName}
                                            </div>
                                        )}
                                        <div>E-Mail: {invoice.billingDraft?.customer?.email || '—'}</div>
                                        <div>Telefon: {invoice.billingDraft?.customer?.phone || '—'}</div>
                                    </div>
                                </div>
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel disabled={sendInvoiceMutation.isPending}>
                                Abbrechen
                            </AlertDialogCancel>
                            <AlertDialogAction
                                onClick={(e) => {
                                    e.preventDefault();
                                    sendInvoiceMutation.mutate({ id: invoice.id });
                                }}
                                disabled={sendInvoiceMutation.isPending}
                            >
                                {sendInvoiceMutation.isPending && (
                                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                )}
                                Senden
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
                    {/* Main Content */}
                    <div className='lg:col-span-2 space-y-6'>
                        {/* Line Items */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Positionen</CardTitle>
                            </CardHeader>
                            <CardContent className='p-0'>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className='w-12'>Pos.</TableHead>
                                            <TableHead>Beschreibung</TableHead>
                                            <TableHead className='text-right'>Netto</TableHead>
                                            <TableHead className='text-right'>MwSt.</TableHead>
                                            <TableHead className='text-right'>Brutto</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {invoice.lineItems.map((item) => (
                                            <TableRow key={item.id}>
                                                <TableCell className='font-mono text-sm'>{item.sequence}</TableCell>
                                                <TableCell>
                                                    <p className='font-medium'>{item.description}</p>
                                                </TableCell>
                                                <TableCell className='text-right font-mono'>
                                                    {formatCurrency(Number(item.totalNet))}
                                                </TableCell>
                                                <TableCell className='text-right font-mono'>
                                                    {formatCurrency(Number(item.vatAmount))}
                                                </TableCell>
                                                <TableCell className='text-right font-mono font-medium'>
                                                    {formatCurrency(Number(item.totalGross))}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                <Separator />
                                <div className='p-4 space-y-2'>
                                    <div className='flex justify-between text-sm'>
                                        <span>Nettobetrag:</span>
                                        <span className='font-mono'>{formatCurrency(invoice.totalNet)}</span>
                                    </div>
                                    <div className='flex justify-between text-sm'>
                                        <span>MwSt.:</span>
                                        <span className='font-mono'>{formatCurrency(invoice.vatAmount)}</span>
                                    </div>
                                    <Separator />
                                    <div className='flex justify-between text-lg font-bold'>
                                        <span>Gesamtbetrag:</span>
                                        <span className={`font-mono ${isRefund ? 'text-red-600' : ''}`}>
                                            {formatCurrency(invoice.totalGross)}
                                        </span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Refund Info */}
                        {invoice.refundSourceInvoice && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Gutschrift für</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className='text-sm'>
                                        Diese Rechnung ist eine Storno-Rechnung für Rechnung{' '}
                                        <span
                                            className='font-mono font-medium cursor-pointer hover:underline text-primary'
                                            onClick={() => navigate(`/invoice/${invoice.refundSourceInvoice!.id}`)}
                                        >
                                            {invoice.refundSourceInvoice.invoiceNumber}
                                        </span>
                                    </p>
                                </CardContent>
                            </Card>
                        )}
                        {invoice.refundInvoices && invoice.refundInvoices.length > 0 && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>{invoice.refundInvoices.length === 1 ? 'Gutschrift vorhanden' : 'Gutschriften vorhanden'}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {invoice.refundInvoices.map((refund) => (
                                        <p key={refund.id} className='text-sm'>
                                            Zu dieser Rechnung existiert eine Storno-Rechnung:{' '}
                                            <span
                                                className='font-mono font-medium cursor-pointer hover:underline text-primary'
                                                onClick={() => navigate(`/invoice/${refund.id}`)}
                                            >
                                                {refund.invoiceNumber}
                                            </span>
                                        </p>
                                    ))}
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className='space-y-6'>
                        {/* Invoice Info */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Rechnungsdetails</CardTitle>
                            </CardHeader>
                            <CardContent className='space-y-4'>
                                <div>
                                    <span className='text-sm text-muted-foreground'>Rechnungsnummer</span>
                                    <p className='font-mono font-medium'>{invoice.invoiceNumber}</p>
                                </div>
                                <div>
                                    <span className='text-sm text-muted-foreground'>Rechnungsdatum</span>
                                    <p className='font-medium'>
                                        {format(new Date(invoice.invoiceDate), 'dd.MM.yyyy', { locale: de })}
                                    </p>
                                </div>
                                <div>
                                    <span className='text-sm text-muted-foreground'>Typ</span>
                                    <div className='mt-1'>
                                        <Badge variant={typeInfo.variant}>{typeInfo.label}</Badge>
                                    </div>
                                </div>
                                <div>
                                    <span className='text-sm text-muted-foreground'>Währung</span>
                                    <p className='font-medium'>{invoice.currency}</p>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Customer Info */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Kunde</CardTitle>
                            </CardHeader>
                            <CardContent className='space-y-2'>
                                <p
                                    className='font-medium cursor-pointer hover:underline text-primary'
                                    onClick={() => navigate(`/customer/${invoice.billingDraft.customer.id}`)}
                                >
                                    {invoice.billingDraft.customer.firstName} {invoice.billingDraft.customer.lastName}
                                </p>
                                {invoice.billingDraft.customer.email && (
                                    <p className='text-sm text-muted-foreground'>
                                        {invoice.billingDraft.customer.email}
                                    </p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Linked Billing Draft */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Auftrag</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p
                                    className='font-mono font-medium cursor-pointer hover:underline text-primary'
                                    onClick={() => navigate(`/billing-draft/${invoice.billingDraftId}`)}
                                >
                                    {invoice.billingDraft.billingDraftNumber}
                                </p>
                                <p className='text-sm text-muted-foreground mt-1'>
                                    Filiale: {invoice.billingDraft.store.name}
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </Content>
    );
};

export default Invoice;
